import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import Button from '../../components/base/Button';

const Portfolio = () => {
  const [activeProject, setActiveProject] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const projects = [
    {
      id: 1,
      title: 'SAM.gov Registration Success',
      category: 'Government Contracting',
      client: 'Technology Startup',
      description: 'Complete SAM.gov registration including CAGE code and UEI for a technology startup seeking federal contracts.',
      results: ['SAM Registration Approved', 'CAGE Code Assigned', 'UEI Processed', 'Ready for Federal Bidding'],
      image: 'https://public.readdy.ai/ai/img_res/69de439de7cc2b114e7148b632057d3e.jpg',
      testimonial: 'Phoenix Achievers Team made the complex SAM registration process seamless. We were approved faster than expected and are now bidding on federal contracts.'
    },
    {
      id: 2,
      title: 'Sutter-Yuba County Grant Win',
      category: 'Grant Writing',
      client: 'Community Nonprofit',
      description: 'Successfully secured a significant grant for a community nonprofit organization in Sutter-Yuba County.',
      results: ['$150,000 Grant Awarded', 'Program Funding Secured', '3-Year Implementation Plan', 'Community Impact Achieved'],
      image: 'https://readdy.ai/api/search-image?query=Grant%20award%20notification%20letter%20and%20celebration%2C%20nonprofit%20organization%20receiving%20funding%20approval%2C%20community%20development%20project%20success%2C%20official%20grant%20documentation%20and%20happy%20team&width=600&height=400&seq=grant-success&orientation=landscape',
      testimonial: 'The grant writing expertise from Phoenix Achievers Team transformed our nonprofit. The $150,000 award has enabled us to expand our community programs significantly.'
    },
    {
      id: 3,
      title: 'Professional Website Portfolio',
      category: 'Web Design',
      client: 'Healthcare Practice',
      description: 'Modern, responsive website design for a healthcare practice with appointment booking and patient portal integration.',
      results: ['Mobile-Responsive Design', 'SEO Optimized', 'Appointment System', '300% Traffic Increase'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20healthcare%20website%20design%20on%20multiple%20devices%2C%20modern%20medical%20practice%20website%20with%20clean%20layout%2C%20appointment%20booking%20system%2C%20responsive%20design%20showcase&width=600&height=400&seq=healthcare-website&orientation=landscape',
      testimonial: 'Our new website has transformed our practice. Patient appointments have increased 300% and we receive more online inquiries than ever before.'
    },
    {
      id: 4,
      title: 'LLC Formation & Compliance',
      category: 'Business Registration',
      client: 'E-commerce Startup',
      description: 'Complete business formation including LLC registration, EIN acquisition, and ongoing compliance setup.',
      results: ['LLC Registered', 'EIN Obtained', 'Operating Agreement', 'Tax Setup Complete'],
      image: 'https://readdy.ai/api/search-image?query=Business%20registration%20documents%20and%20certificates%2C%20LLC%20formation%20paperwork%2C%20corporate%20filing%20success%2C%20official%20business%20license%20and%20registration%20stamps&width=600&height=400&seq=llc-formation&orientation=landscape',
      testimonial: 'Phoenix Achievers Team handled everything from start to finish. Our LLC was registered quickly and all compliance requirements were met perfectly.'
    },
    {
      id: 5,
      title: 'Nonprofit Capability Statement',
      category: 'Proposal Writing',
      client: 'Educational Nonprofit',
      description: 'Professional capability statement development for educational nonprofit seeking government education contracts.',
      results: ['Professional Design', 'Key Differentiators Highlighted', 'Contract Ready', 'Winning Proposals'],
      image: 'https://readdy.ai/api/search-image?query=Professional%20capability%20statement%20document%20layout%2C%20business%20proposal%20design%20with%20charts%20and%20credentials%2C%20corporate%20presentation%20materials%2C%20government%20contracting%20documents&width=600&height=400&seq=capability-statement&orientation=landscape',
      testimonial: 'The capability statement opened doors to government contracts we never thought possible. The professional presentation made all the difference.'
    },
    {
      id: 6,
      title: 'E-commerce Store Launch',
      category: 'E-commerce Consulting',
      client: 'Fashion Retailer',
      description: 'Complete e-commerce store setup with payment processing, inventory management, and digital marketing strategy.',
      results: ['Shopify Store Built', 'Payment Integration', 'Inventory System', '$50K First Month Sales'],
      image: 'https://readdy.ai/api/search-image?query=Modern%20e-commerce%20fashion%20store%20website%2C%20online%20shopping%20interface%20with%20product%20gallery%2C%20professional%20retail%20website%20design%2C%20shopping%20cart%20and%20checkout%20system&width=600&height=400&seq=ecommerce-store&orientation=landscape',
      testimonial: 'Our e-commerce store generated $50,000 in sales the first month after launch. The setup was flawless and customer-friendly.'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      company: 'TechStart Solutions',
      text: 'Phoenix Achievers Team transformed our business registration process. What seemed impossible became straightforward with their expertise.',
      rating: 5
    },
    {
      name: 'Michael Rodriguez',
      company: 'Community Health Nonprofit',
      text: 'The grant writing services exceeded our expectations. We secured funding that will impact our community for years to come.',
      rating: 5
    },
    {
      name: 'Jennifer Chen',
      company: 'Global Consulting LLC',
      text: 'Professional, reliable, and results-driven. Phoenix Achievers Team delivered exactly what they promised, on time and on budget.',
      rating: 5
    },
    {
      name: 'David Thompson',
      company: 'Innovation Partners',
      text: 'The SAM registration process was complex, but their team made it seamless. We are now successfully bidding on federal contracts.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-['Poppins']">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Our Portfolio</h1>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed mb-8">
              Real results for real clients. See how we've helped businesses and nonprofits achieve their goals.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm lg:text-base">
              <span className="bg-white/20 px-4 py-2 rounded-full">300+ Successful Projects</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Global Client Base</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Proven Results</span>
            </div>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Success Stories</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From startups to established organizations, we've helped clients across industries achieve their objectives.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {projects.map((project) => (
                <div 
                  key={project.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer"
                  onClick={() => setActiveProject(activeProject === project.id ? null : project.id)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {project.category}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-black mb-2">{project.title}</h3>
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.results.map((result, index) => (
                        <span 
                          key={index}
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                        >
                          ✓ {result}
                        </span>
                      ))}
                    </div>

                    {activeProject === project.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-700 italic mb-2">Client Testimonial:</p>
                        <p className="text-gray-600">"{project.testimonial}"</p>
                        <p className="text-sm text-gray-500 mt-2">- {project.client}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Our Impact</h2>
              <p className="text-xl text-gray-600">Numbers that speak for themselves</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center bg-white rounded-xl p-8 shadow-lg">
                <div className="text-4xl font-bold text-red-600 mb-2">300+</div>
                <div className="text-gray-700 font-semibold">Successful Projects</div>
              </div>
              
              <div className="text-center bg-white rounded-xl p-8 shadow-lg">
                <div className="text-4xl font-bold text-black mb-2">15+</div>
                <div className="text-gray-700 font-semibold">Countries Served</div>
              </div>
              
              <div className="text-center bg-white rounded-xl p-8 shadow-lg">
                <div className="text-4xl font-bold text-red-600 mb-2">$5M+</div>
                <div className="text-gray-700 font-semibold">Grants Secured</div>
              </div>
              
              <div className="text-center bg-white rounded-xl p-8 shadow-lg">
                <div className="text-4xl font-bold text-black mb-2">98%</div>
                <div className="text-gray-700 font-semibold">Client Satisfaction</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials Carousel */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">What Our Clients Say</h2>
              <p className="text-xl text-gray-600">Real feedback from real clients</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-black">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Showcase */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Before & After</h2>
              <p className="text-xl text-gray-600">See the transformation we bring to businesses</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-black mb-4">SAM.gov Registration</h3>
                  <div className="space-y-4">
                    <div className="border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-600 mb-2">BEFORE:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• Unable to bid on federal contracts</li>
                        <li>• Complex registration process</li>
                        <li>• Missing required documentation</li>
                        <li>• No CAGE code or UEI</li>
                      </ul>
                    </div>
                    <div className="border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-600 mb-2">AFTER:</h4>
                      <ul className="text-gray-600 space-y-1">
                        <li>• SAM registration approved</li>
                        <li>• CAGE code assigned</li>
                        <li>• UEI processed successfully</li>
                        <li>• Ready for federal bidding</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div>
                  <img 
                    src="https://readdy.ai/api/search-image?query=Business%20transformation%20success%20chart%20showing%20before%20and%20after%20results%2C%20professional%20growth%20metrics%2C%20government%20contracting%20achievement%20visualization%2C%20success%20comparison%20graphics&width=500&height=400&seq=transformation-chart&orientation=squarish"
                    alt="Business Transformation Results"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Join Our Success Stories?</h2>
            <p className="text-xl mb-8 opacity-90">
              Let us help you achieve the same results our clients have experienced. Your success story could be next.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open('https://www.fiverr.com/users/abiodun136', '_blank')}
                className="bg-red-600 text-white hover:bg-red-700 px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                Start Your Project
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-semibold whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Discuss Your Goals
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Portfolio;